import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatEuros from '@UI/Helpers/formatEuros';
import useAiCostSummary from '@UI/Pages/Administration/AiCosts/Hooks/useAiCostSummary';
import describeScope from '@UI/Pages/Administration/AiCosts/Helpers/describeScope';
import CostSplit from '@UI/Pages/Administration/AiCosts/CostSummary/CostSplit';
import MonthlyLimit from '@UI/Pages/Administration/AiCosts/CostSummary/MonthlyLimit';
import Evolution from '@UI/Pages/Administration/AiCosts/CostSummary/SummaryCard/Evolution';
import UnpricedNotice from '@UI/Pages/Administration/AiCosts/CostSummary/SummaryCard/UnpricedNotice';
import type AiCostsFilterView from '@UI/Pages/Administration/AiCosts/Filters/AiCostsFilterView';
import { css } from '@emotion/react';
import { Receipt } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const cardStyle = css`
	position: relative;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	padding: 1.25rem 1.5rem;
	border-radius: ${theme.radii.xlarge};
	border: 1px solid ${theme.colors.border};
	background-color: ${theme.colors.surface};
`;

const stripeStyle = css`
	position: absolute;
	inset: 0 0 auto;
	display: flex;
	height: 4px;
`;

const scopeStyle = css`
	margin: 0.5rem 0 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const bodyStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
	gap: 1.5rem;
`;

const totalColumnStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	align-items: flex-start;
`;

const amountStyle = css`
	margin: 0;
	font-family: ${theme.fonts.display};
	font-size: 3rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1;
	letter-spacing: -0.04em;
	color: ${theme.colors.accentDark};
`;

type Props = {
	readonly accountId: number;
	readonly filters: AiCostsFilterView;
};

/** Reads the summary of one period and scope, and lays out the total, the split and the monthly limit. */
const SummaryCard: React.FC<Props> = ({ accountId, filters }) => {
	const filterOptions = useSelector((state) => state.aiCostsStore.filterOptions);

	const { data, error, refetch } = useAiCostSummary(accountId, filters.summaryQueryParams, true);

	const scopeDescription = React.useMemo(
		() => describeScope(filters, filterOptions),
		[filters, filterOptions],
	);

	if (error !== null) {
		return <ErrorState title="Indisponible tant que les coûts ne sont pas chargés." onRetry={refetch} />;
	}

	if (data === null) {
		return <Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />;
	}

	return (
		<ConditionalLoader
			data={data.hasActivity && data}
			fallback={<EmptyState title="Aucun coût sur la période." icon={<Receipt size={28} />} />}
		>
			{(summary) => (
				<div css={cardStyle}>
					<div css={stripeStyle} aria-hidden="true">
						{theme.aiCosts.summaryStripe.map((color) => (
							<span key={color} style={{ flex: 1, backgroundColor: color }} />
						))}
					</div>
					<div>
						<Eyebrow>Coût estimé de la période</Eyebrow>
						<p css={scopeStyle}>{scopeDescription}</p>
					</div>
					<div css={bodyStyle}>
						<div css={totalColumnStyle}>
							<p css={amountStyle}>{formatEuros(summary.costAmount)}</p>
							<Evolution summary={summary} />
							<UnpricedNotice unpricedItemCount={summary.unpricedItemCount} />
						</div>
						<CostSplit summary={summary} />
					</div>
					<MonthlyLimit accountId={accountId} summary={summary} filters={filters} />
				</div>
			)}
		</ConditionalLoader>
	);
};

export default SummaryCard;
